import { addBreadcrumb, setExtras } from '@sentry/react';
import type { Merge, SetRequired } from 'type-fest';
import { NetworkError } from 'types/networkError';
import { setExtraInfo, setStackTrace } from 'utils/exception/errorUtils';
import { captureException, isNetworkError, UNHANDLED_ERROR } from 'utils/exception/index';
import { AxiosErrorHandler, InternalErrorType, SUCCESS_STATUS } from 'utils/exception/types';

import { Codeowners } from 'components/Common/Error/Codeowners';

/**
 * @deprecated Using this value is not suggested.
 * Instead please add proper error handlers for possible network errors.
 *
 * If you decide to use this value it means that errors like 401 / 404/ 500 / 502
 * are going to be caught and sent to the Sentry.
 */
export const DISABLE_ERROR_HANDLER = 'disableErrorHandler';

const getInternalErrorOutput = <DataType, NetworkErrors extends string>(
  error: SetRequired<NetworkError<DataType>, 'response'>,
  codeOwners: Codeowners,
  errorHandler: AxiosErrorHandler<NetworkErrors> | typeof DISABLE_ERROR_HANDLER
): InternalErrorType<NetworkErrors> | undefined => {
  try {
    const onErrorResult = errorHandler === DISABLE_ERROR_HANDLER ? null : errorHandler(error, codeOwners);

    if (onErrorResult && onErrorResult !== UNHANDLED_ERROR) {
      addBreadcrumb({
        data: onErrorResult,
      });

      setExtras({
        response: error.response,
      });

      return {
        status: onErrorResult,
        error: error.response,
        data: null,
      } as InternalErrorType<NetworkErrors>;
    }
  } catch (internalError) {
    captureException(internalError, codeOwners);
  }
};

export function catchNetworkExceptions<DataType, NetworkErrors extends string = never>(
  func: () => Promise<DataType>,
  codeOwners: Codeowners,
  errorHandler: AxiosErrorHandler<NetworkErrors> | typeof DISABLE_ERROR_HANDLER
): Promise<Merge<DataType, { status: 'success'; error: null }> | InternalErrorType<NetworkErrors>> {
  const newError = new Error();

  // Do not make it async function, otherwise we will lose the Error stack trace
  return func()
    .then((response) => ({ ...response, status: SUCCESS_STATUS }))

    .catch((error: unknown) => {
      if (!isNetworkError(error)) {
        throw error;
      }

      // If the error happens in errorHandler function, catch it and log with team's codeowners
      const handlerError = getInternalErrorOutput(error, codeOwners, errorHandler);
      if (handlerError) {
        return handlerError as any;
      }

      try {
        setExtraInfo(error);
        setStackTrace(error, newError);
      } catch (internalError) {
        // Send exception to CI team because there is a bad code inside setExtra info
        captureException(internalError, Codeowners.CUSTOMER_INTAKE);
      }

      // Send exception caught in async function and assign codeowners
      captureException(newError, codeOwners);

      throw newError;
    });
}
